import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import Swal from "sweetalert2";
import { createNoticiaEvento, updateNoticiaEvento } from "../services/noticiasEventosService";

const getNoticiaEventoId = (item) =>
  item?.id ?? item?.Id ?? item?.idNoticiaEvento ?? item?.IdNoticiaEvento ?? item?.idNoticiasEventos;

const toISO = (v) => {
  if (!v) return "";
  if (typeof v === "string" && v.includes("T")) return v.slice(0, 19);
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 19);
};

const initialFormState = {
  Titulo: "",
  Resumen: "",
  Contenido: "",
  ImagenUrl: "",
  Tipo: "Noticia",
  CategoriaId: 0,
  FechaEventoInicio: "",
  FechaEventoFin: "",
  Ubicacion: "",
  Modalidad: "Presencial",
  EnlaceEvento: "",
  FechaPublicacion: new Date().toISOString().slice(0, 16),
  FechaExpiracion: "",
  EsDestacado: 0,
  EsPublico: 1,
  Estado: "Borrador",
};

const NoticiasEventosForm = ({ noticiaEventoData, onSuccess }) => {
  const isEdit = Boolean(noticiaEventoData);
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (noticiaEventoData) {
      const d = noticiaEventoData;
      setFormData({
        Titulo: d.Titulo ?? "",
        Resumen: d.Resumen ?? "",
        Contenido: d.Contenido ?? "",
        ImagenUrl: d.ImagenUrl ?? d.imagenUrl ?? "",
        Tipo: d.Tipo ?? "Noticia",
        CategoriaId: Number(d.CategoriaId) || 0,
        FechaEventoInicio: toISO(d.FechaEventoInicio) ? toISO(d.FechaEventoInicio).slice(0, 16) : "",
        FechaEventoFin: toISO(d.FechaEventoFin) ? toISO(d.FechaEventoFin).slice(0, 16) : "",
        Ubicacion: d.Ubicacion ?? "",
        Modalidad: d.Modalidad ?? "Presencial",
        EnlaceEvento: d.EnlaceEvento ?? "",
        FechaPublicacion: d.FechaPublicacion ? toISO(d.FechaPublicacion).slice(0, 16) : new Date().toISOString().slice(0, 16),
        FechaExpiracion: d.FechaExpiracion ? toISO(d.FechaExpiracion).slice(0, 16) : "",
        EsDestacado: Number(d.EsDestacado) === 1 ? 1 : 0,
        EsPublico: Number(d.EsPublico) === 1 ? 1 : 0,
        Estado: d.Estado ?? "Borrador",
      });
    } else {
      setFormData(initialFormState);
    }
  }, [noticiaEventoData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsed =
      name === "CategoriaId" ? (value === "" ? 0 : Number(value)) :
      name === "EsDestacado" || name === "EsPublico" ? (e.target.checked ? 1 : 0) :
      value;
    setFormData((prev) => ({ ...prev, [name]: parsed }));
  };

  const buildPayload = () => {
    const toApiDate = (v) => {
      if (!v) return v;
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : d.toISOString();
    };
    const toApiFechaPublicacion = (v) => {
      if (!v) return v;
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
    };
    return {
      Titulo: formData.Titulo.trim(),
      Resumen: formData.Resumen.trim(),
      Contenido: formData.Contenido.trim(),
      ImagenUrl: formData.ImagenUrl.trim() || "",
      Tipo: formData.Tipo,
      CategoriaId: formData.CategoriaId,
      FechaEventoInicio: formData.FechaEventoInicio ? toApiDate(formData.FechaEventoInicio) : null,
      FechaEventoFin: formData.FechaEventoFin ? toApiDate(formData.FechaEventoFin) : null,
      Ubicacion: formData.Ubicacion.trim() || "",
      Modalidad: formData.Modalidad,
      EnlaceEvento: formData.EnlaceEvento.trim() || "",
      FechaPublicacion: formData.FechaPublicacion ? toApiFechaPublicacion(formData.FechaPublicacion) : null,
      FechaExpiracion: formData.FechaExpiracion ? toApiDate(formData.FechaExpiracion) : null,
      EsDestacado: formData.EsDestacado,
      EsPublico: formData.EsPublico,
      Estado: formData.Estado,
    };
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!formData.Titulo?.trim()) {
      return Swal.fire("Error", "El título es obligatorio", "error");
    }
    try {
      setLoading(true);
      const payload = buildPayload();
      if (isEdit) {
        const id = getNoticiaEventoId(noticiaEventoData);
        if (id == null) return Swal.fire("Error", "No se pudo identificar el registro.", "error");
        const response = await updateNoticiaEvento(id, payload);
        if (response?.success !== false) {
          Swal.fire({ icon: "success", title: "Actualizado", text: "Los cambios se guardaron correctamente." });
          onSuccess?.();
        } else {
          Swal.fire("Error", response?.message || "Error al actualizar", "error");
        }
      } else {
        const response = await createNoticiaEvento(payload);
        if (response?.success !== false) {
          Swal.fire({ icon: "success", title: "Creado", text: "La noticia/evento se ha creado correctamente." });
          setFormData(initialFormState);
          onSuccess?.();
        } else {
          Swal.fire("Error", response?.message || "Error al crear", "error");
        }
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Error de conexión";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData(initialFormState);
  };

  return (
    <Card sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "none" }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <ArticleIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">{isEdit ? "Editar noticia/evento" : "Nueva noticia o evento"}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 720 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Título" name="Titulo" value={formData.Titulo} onChange={handleChange} required placeholder="Título de la noticia" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Resumen" name="Resumen" value={formData.Resumen} onChange={handleChange} multiline rows={2} placeholder="Resumen breve" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Contenido" name="Contenido" value={formData.Contenido} onChange={handleChange} multiline rows={4} placeholder="Contenido completo" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="URL de imagen" name="ImagenUrl" value={formData.ImagenUrl} onChange={handleChange} placeholder="https://..." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select name="Tipo" value={formData.Tipo} label="Tipo" onChange={handleChange}>
                  <MenuItem value="Noticia">Noticia</MenuItem>
                  <MenuItem value="Evento">Evento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Categoría (ID)" name="CategoriaId" type="number" inputProps={{ min: 0 }} value={formData.CategoriaId} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Fecha publicación" name="FechaPublicacion" type="datetime-local" value={formData.FechaPublicacion} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Fecha expiración" name="FechaExpiracion" type="datetime-local" value={formData.FechaExpiracion} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Inicio del evento" name="FechaEventoInicio" type="datetime-local" value={formData.FechaEventoInicio} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Fin del evento" name="FechaEventoFin" type="datetime-local" value={formData.FechaEventoFin} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Ubicación" name="Ubicacion" value={formData.Ubicacion} onChange={handleChange} placeholder="Lugar del evento" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Modalidad</InputLabel>
                <Select name="Modalidad" value={formData.Modalidad} label="Modalidad" onChange={handleChange}>
                  <MenuItem value="Presencial">Presencial</MenuItem>
                  <MenuItem value="Virtual">Virtual</MenuItem>
                  <MenuItem value="Híbrido">Híbrido</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Enlace del evento" name="EnlaceEvento" value={formData.EnlaceEvento} onChange={handleChange} placeholder="https://..." />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select name="Estado" value={formData.Estado} label="Estado" onChange={handleChange}>
                  <MenuItem value="Borrador">Borrador</MenuItem>
                  <MenuItem value="Publicado">Publicado</MenuItem>
                  <MenuItem value="Borrado">Borrado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox name="EsDestacado" checked={formData.EsDestacado === 1} onChange={handleChange} />}
                label="Es destacado"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox name="EsPublico" checked={formData.EsPublico === 1} onChange={handleChange} />}
                label="Es público"
              />
            </Grid>
            <Grid item xs={12}>
              <Box mt={2} display="flex" gap={2} justifyContent="flex-end">
                {!isEdit && (
                  <Button variant="outlined" onClick={handleClear} type="button">Limpiar</Button>
                )}
                <Button variant="contained" color="primary" type="submit" disabled={loading}>
                  {loading ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NoticiasEventosForm;
